import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import FetchModule from '../../Model/Share/Network/FetchModule';
import { callingCodeToCountry } from '../../Utils/phoneUtils';
import { bankList, formatAccountNumber } from '../../Utils/bankList';
import { formatBusinessNumber } from '../../Utils/businessNumber';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';
import CountryCodeSelectBox from '../../Component/Common/CountryCodeSelectBox';
import BankSelectBox from '../../Component/Common/BankSelectBox';

import { ReactComponent as UploadDocument } from '../../image/UploadDocument.svg';
import { ReactComponent as CheckSvg } from '../../image/Check.svg';
import BankDefaultImage from '../../image/BankDefault.svg';

// 사업자등록번호 가운데 2자리(YY)로 사업자 유형 판별 - 01~79: 개인사업자, 그 외: 법인(영리/비영리/외국법인 포함) //
function getBusinessType(value) {
  const num = value.replace(/[^0-9]/g, '');
  const typeCode = parseInt(num.slice(3, 5), 10);

  if (typeCode >= 1 && typeCode <= 79) return 'INDIVIDUAL_BUSINESS';

  return 'CORPORATE';
}

// 한국 사업자등록번호 검증 - 숫자만 추출 후 10자리 + 국세청 체크섬(가중치 [1,3,7,1,3,7,1,3,5] + d[8]*5 십의 자리) //
const BUSINESS_NUMBER_WEIGHTS = [1, 3, 7, 1, 3, 7, 1, 3, 5];

function isValidBusinessNumber(value) {
  if (!value) return false;

  const num = value.replace(/[^0-9]/g, '');

  if (num.length !== 10) return false;

  const digits = num.split('').map(Number);

  let sum = 0;

  for (let i = 0; i < 9; i++) {
    sum += digits[i] * BUSINESS_NUMBER_WEIGHTS[i];
  }

  sum += Math.floor((digits[8] * 5) / 10);

  const checkDigit = (10 - (sum % 10)) % 10;

  return checkDigit === digits[9];
}

export default function LinkoPayApplyModal(props) {
  const {
    mode = 'apply',
    businessRegistrationNumber,
    representativeName,
    linkopayEmail,
    bankName,
    accountNumber: prefillAccountNumber,
    settleCycle,
    storeName: prefillStoreName,
    businessLicenseUrl,
    businessLicenseFileName: prefillBusinessLicenseFileName,
    phone: prefillPhone,
    countryCode: prefillCountryCode,
    holderName: prefillHolderName,
    onClickExit,
    onClickSubmit
  } = props;

  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const isApply = mode === 'apply';
  const isReviewing = mode === 'reviewing';
  const isActive = mode === 'active';
  const isRejected = mode === 'rejected';
  // 대기/승인 상태에서 "재심사 요청하기" 1차 클릭 시 편집 모드로 진입하기 위한 오버라이드 //
  const [editingOverride, setEditingOverride] = useState(false);
  const isEditable = isApply || isRejected || editingOverride;
  const showTerms = isEditable;
  const isEditTrigger = (isReviewing || isActive) && !editingOverride;

  const normalizedPrefillBankName = bankName ? bankName.replace(/\s+/g, '') : '';
  const prefillBank = bankName ? (bankList.find(bank => bank.name.replace(/\s+/g, '') === normalizedPrefillBankName) || null) : null;

  const [storeName, setStoreName] = useState(prefillStoreName || '');
  const [businessNumber, setBusinessNumber] = useState(businessRegistrationNumber || '');
  const [businessLicenseFileName, setBusinessLicenseFileName] = useState(prefillBusinessLicenseFileName || '');
  const [businessLicenseFile, setBusinessLicenseFile] = useState(null);
  const [ownerName, setOwnerName] = useState(representativeName || '');
  const [accountNumber, setAccountNumber] = useState(prefillAccountNumber || '');
  const [contactEmail, setContactEmail] = useState(linkopayEmail || '');
  const [contactPhone, setContactPhone] = useState(prefillPhone || '');

  const [selectedCountry, setSelectedCountry] = useState({ countryCode: prefillCountryCode || '+82' });
  const [selectedBank, setSelectedBank] = useState(prefillBank);
  const [selectedCycle, setSelectedCycle] = useState(settleCycle || 5);

  const [lookupLinkAppId, setLookupLinkAppId] = useState(undefined);
  const [holderName, setHolderName] = useState(prefillHolderName || undefined);
  const [isAccountNumberInvalid, setIsAccountNumberInvalid] = useState(false);
  const [businessStatusResult, setBusinessStatusResult] = useState(undefined);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [isAccountConfirmRequired, setIsAccountConfirmRequired] = useState(false);
  const [isPhoneConfirmRequired, setIsPhoneConfirmRequired] = useState(false);

  const [isAgreedService, setIsAgreedService] = useState(false);
  const [isAgreedPrivacy, setIsAgreedPrivacy] = useState(false);
  const [isAgreedEft, setIsAgreedEft] = useState(false);
  const [isAgreedSettlement, setIsAgreedSettlement] = useState(false);
  const [isAgreedAutoPay, setIsAgreedAutoPay] = useState(false);
  const [isAgreedPg, setIsAgreedPg] = useState(false);

  const [expandedTerms, setExpandedTerms] = useState({});

  const BusinessLicenseInputRef = useRef();

  const ALLOWED_LICENSE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'heic', 'hevc'];
  const MAX_LICENSE_FILE_SIZE = 10 * 1024 * 1024;

  const hasLookupResult = lookupLinkAppId !== undefined;
  const isAgreedAll =
    isAgreedService && isAgreedPrivacy && isAgreedEft &&
    isAgreedSettlement && isAgreedAutoPay && isAgreedPg;

  const country = callingCodeToCountry[selectedCountry.countryCode];
  const isBusinessNumberInvalid = isSubmitAttempted && !isValidBusinessNumber(businessNumber);

  // 번호 또는 국가가 바뀌면 이전 조회 결과는 무효 - 다시 확인 누르도록 미조회 상태로 되돌림 //
  useEffect(() => {
    setLookupLinkAppId(undefined);
    setIsPhoneConfirmRequired(false);
  }, [contactPhone, selectedCountry]);

  // 사업자등록번호가 바뀌면 이전 상태조회 결과는 무효 - 다시 확인 누르도록 미조회 상태로 되돌림 //
  useEffect(() => {
    setBusinessStatusResult(undefined);
  }, [businessNumber]);

  // 조회 모드에서는 CountryCodeSelectBox가 마운트되지 않아 flagUrl이 비어있음 - 1회 fetch로 채움 //
  useEffect(() => {
    if (isEditable) return;
    if (selectedCountry.flagUrl) return;

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataOfCoachVersion2('sms_cert/country', 'GET');

        if (responseData.status === 200) {
          const match = responseData.data.find(country => country.countryCode === selectedCountry.countryCode);
          if (match) setSelectedCountry(match);
        }
      }
    )();
  }, []);

  // 계좌/은행 변경 시 예금주 조회 결과 무효 - 숫자 외 입력 차단 //
  function onChangeAccountNumber(e) {
    setAccountNumber(e.target.value.replace(/[^0-9]/g, ''));
    setHolderName(undefined);
    setIsAccountNumberInvalid(false);
    setIsAccountConfirmRequired(false);
  }

  function onChangeSelectedBank(bank) {
    setSelectedBank(bank);
    setHolderName(undefined);
    setIsAccountNumberInvalid(false);
    setIsAccountConfirmRequired(false);
  }

  // 개별 약관 펼침 토글 - 클릭 시 본문 보이기/숨기기 //
  function onToggleExpandedTerm(key) {
    setExpandedTerms(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // 전체 동의 토글 - 모든 약관 체크 동기화 //
  function onClickAgreeAll() {
    const next = !isAgreedAll;

    setIsAgreedService(next);
    setIsAgreedPrivacy(next);
    setIsAgreedEft(next);
    setIsAgreedSettlement(next);
    setIsAgreedAutoPay(next);
    setIsAgreedPg(next);
  }

  // 사업자등록증 찾기 클릭 - 숨김 파일 input 트리거 //
  function onClickFileBrowse() {
    if (BusinessLicenseInputRef.current) BusinessLicenseInputRef.current.click();
  }

  // 사업자등록증 미리보기 - 새 탭에서 URL 오픈 //
  function onClickReadonlyBusinessLicense() {
    if (businessLicenseUrl) window.open(businessLicenseUrl, '_blank', 'noopener,noreferrer');
  }

  // 파일 선택 - 확장자/크기 검증 후 상태 반영 //
  function onChangeBusinessLicense(e) {
    const file = e.target.files?.[0];

    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    if (!ALLOWED_LICENSE_EXTENSIONS.includes(extension)) {
      alert(t('myStoreSetting.linkoPay.applyModal.fileTypeInvalidAlert'));
      e.target.value = '';
      return;
    }

    if (file.size > MAX_LICENSE_FILE_SIZE) {
      alert(t('myStoreSetting.linkoPay.applyModal.fileSizeExceededAlert'));
      e.target.value = '';
      return;
    }

    setBusinessLicenseFile(file);
    setBusinessLicenseFileName(file.name);
    e.target.value = '';
  }

  // 로컬 시간 ISO 포맷 (yyyy-MM-ddTHH:mm:ss, 타임존 미포함) //
  function formatLocalDateTime(date) {
    const pad = (n) => String(n).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  // 사업자등록번호 확인 버튼 - 국세청 상태조회 (200 active / 201 inactive(휴업·폐업·미등록) / 400 형식 오류 / 401 인증 만료) //
  // 클라이언트 체크섬 사전 검증으로 형식 오류는 호출 없이 즉시 처리 //
  function onClickConfirmBusinessNumber() {
    if (!isValidBusinessNumber(businessNumber)) {
      setBusinessStatusResult('invalid_format');
      return;
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('account/business-status', 'POST', { businessNumber });

        if (responseData.status === 200) setBusinessStatusResult('active');
        else if (responseData.status === 201) setBusinessStatusResult('inactive');
        else if (responseData.status === 400) setBusinessStatusResult('invalid_format');
        else if (responseData.status === 401) history.push('/');
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }

  // 계좌 확인 버튼 - 은행/계좌번호로 예금주 조회 //
  function onClickConfirmAccount() {
    if (!selectedBank) {
      alert(t('myStoreSetting.linkoPay.bankRequired'));
      return;
    }

    if (!accountNumber) {
      alert(t('myStoreSetting.linkoPay.accountRequired'));
      return;
    }

    setIsAccountConfirmRequired(false);

    const requestData = {
      bankCode: selectedBank.code,
      accountNumber: accountNumber
    };

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('account/holder-name', 'POST', requestData);

        if (responseData.status === 200 && responseData.data?.holderName) {
          setHolderName(responseData.data.holderName);
          setIsAccountNumberInvalid(false);
        }
        else if (responseData.status === 201) {
          setHolderName(undefined);
          setIsAccountNumberInvalid(true);
        }
        else if (responseData.status === 401) history.push('/');
        else {
          setHolderName(null);
          setIsAccountNumberInvalid(false);
        }
      }
    )();
  }

  // 연락처 확인 버튼 - 입력한 번호로 링코앱 가입 여부 조회 //
  function onClickConfirmPhone() {
    if (!contactPhone) {
      alert(t('myStoreSetting.alert.phoneRequired'));
      return;
    }

    setIsPhoneConfirmRequired(false);

    const requestData = {
      phone: contactPhone,
      countryIso: callingCodeToCountry[selectedCountry.countryCode]
    };

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('account/link_app/find', 'POST', requestData);

        if (responseData.status === 200) setLookupLinkAppId(responseData.data?.linkAppId ?? null);
        else if (responseData.status === 201) setLookupLinkAppId(null);
        else if (responseData.status === 401) history.push('/');
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }

  // 심사 요청 버튼 - 대기/승인 상태 1차 클릭은 편집 모드로만 전환, 이후 클릭은 빈 필드/은행/예금주/약관 검증(미충족 시 인라인 빨강 표시) 후 multipart 전송 //
  function onClickSubmitButton() {
    if (isEditTrigger) {
      setEditingOverride(true);
      return;
    }

    setIsSubmitAttempted(true);

    // 은행/계좌·연락처는 입력했지만 확인 버튼을 안 눌러 미조회 상태면 "확인 버튼을 눌러주세요" 노출 //
    setIsAccountConfirmRequired(!!selectedBank && !!accountNumber && holderName === undefined && !isAccountNumberInvalid);
    setIsPhoneConfirmRequired(!!contactPhone && !hasLookupResult);

    const hasEmptyRequired =
      !storeName || !businessLicenseFileName ||
      !ownerName || !accountNumber || !contactEmail || !contactPhone ||
      !selectedBank || !holderName || !hasLookupResult || !isAgreedAll;

    if (hasEmptyRequired) return;

    if (!isValidBusinessNumber(businessNumber)) return;

    if (businessStatusResult !== 'active') return;

    if (!businessLicenseFile) {
      alert(t('myStoreSetting.linkoPay.applyModal.fileRequiredAlert'));
      return;
    }

    const now = formatLocalDateTime(new Date());

    const data = {
      businessRegistrationNumber: businessNumber,
      businessType: getBusinessType(businessNumber),
      representativeName: ownerName,
      email: contactEmail,
      contactPhone: contactPhone,
      country: country,
      bankCode: selectedBank.code,
      bankName: selectedBank.name,
      accountNumber: accountNumber,
      holderName: holderName,
      settlementCycle: selectedCycle,
      linkAppId: lookupLinkAppId ?? null,
      agreedTerms: {
        service: now,
        privacy: now,
        eft: now,
        settlement: now,
        autopay: now,
        pg: now
      }
    };

    const formData = new FormData();
    formData.append('businessLicense', businessLicenseFile);
    formData.append('data', JSON.stringify(data));

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postFormDataVersion2('account/submit_linkopay', formData);

        if (responseData.status === 200) {
          alert(t('myStoreSetting.linkoPay.applyModal.submitSuccessAlert'));
          if (onClickSubmit) onClickSubmit(responseData.data);
        }
        else if (responseData.status === 401) history.push('/');
        else alert(responseData.error || t('myStoreSetting.linkoPay.applyModal.submitFailedAlert'));
      }
    )();
  }

  // 상태 핀 렌더 - 활성 시 배경색 적용, 비활성 시 회색 핀 //
  function renderStatusPill(isActivePill, label, activeBackground = theme.primary) {
    if (isActivePill) {
      return (
        <StatusPill backgroundColor={activeBackground}>
          <StatusDot backgroundColor={theme.subBackground} />
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground}>
            {label}
          </Text>
        </StatusPill>
      );
    }

    return (
      <StatusPillInactive>
        <StatusDot backgroundColor={theme.subText} />
        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
          {label}
        </Text>
      </StatusPillInactive>
    );
  }

  const termsItems = [
    { key: 'service', checked: isAgreedService, setChecked: setIsAgreedService, labelKey: 'termsService', contentKey: 'termsServiceContent' },
    { key: 'privacy', checked: isAgreedPrivacy, setChecked: setIsAgreedPrivacy, labelKey: 'termsPrivacy', contentKey: 'termsPrivacyContent' },
    { key: 'eft', checked: isAgreedEft, setChecked: setIsAgreedEft, labelKey: 'termsEft', contentKey: 'termsEftContent' },
    { key: 'settlement', checked: isAgreedSettlement, setChecked: setIsAgreedSettlement, labelKey: 'termsSettlement', contentKey: 'termsSettlementContent' },
    { key: 'autopay', checked: isAgreedAutoPay, setChecked: setIsAgreedAutoPay, labelKey: 'termsAutoPay', contentKey: 'termsAutoPayContent' },
    { key: 'pg', checked: isAgreedPg, setChecked: setIsAgreedPg, labelKey: 'termsPg', contentKey: 'termsPgContent' }
  ];

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <TitleWrapper>
          <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>
            {isApply ? t('myStoreSetting.linkoPay.applyModal.title') : t('myStoreSetting.linkoPay.applyModal.titleModify')}
          </Text>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
            {isApply ? t('myStoreSetting.linkoPay.applyModal.description') : t('myStoreSetting.linkoPay.applyModal.descriptionModify')}
          </Text>
        </TitleWrapper>

        <StatusPillRow>
          {renderStatusPill(isApply || editingOverride, t('myStoreSetting.linkoPay.applyModal.statusApply'))}
          {renderStatusPill(isReviewing && !editingOverride, t('myStoreSetting.linkoPay.applyModal.statusWait'))}
          {
            isRejected ?
              renderStatusPill(true, t('myStoreSetting.linkoPay.applyModal.statusReject'), theme.pointRed)
              :
              renderStatusPill(isActive && !editingOverride, t('myStoreSetting.linkoPay.applyModal.statusApprove'))
          }
        </StatusPillRow>

        <ScrollableContent scrollBarColor={theme.scrollBar}>
          <SectionHeaderRow>
            <SectionDot backgroundColor={theme.primary} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('myStoreSetting.linkoPay.applyModal.checklistTitle')}
            </Text>
          </SectionHeaderRow>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelStoreName')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InputBox
                    borderColor={isSubmitAttempted && !storeName ? theme.pointRed : theme.line}
                    backgroundColor={theme.subBackground}>
                    <InputField
                      color={theme.text}
                      $autofillBackground={theme.subBackground}
                      $placeholderColor={isSubmitAttempted && !storeName ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                      value={storeName}
                      onChange={(e) => setStoreName(e.target.value)}
                      placeholder={t('myStoreSetting.linkoPay.applyModal.placeholderStoreName')} />
                  </InputBox>
                  :
                  <ReadonlyValueBox>
                    <ReadonlyValueText color={theme.text}>{storeName || '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelBusinessNumber')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InlineControlRow>
                    <BusinessNumberInputBox
                      borderColor={(isBusinessNumberInvalid || (businessStatusResult !== undefined && businessStatusResult !== 'active')) ? theme.pointRed : theme.line}
                      backgroundColor={theme.subBackground}>
                      <InputField
                        color={theme.text}
                        $autofillBackground={theme.subBackground}
                        $placeholderColor={isBusinessNumberInvalid ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                        value={formatBusinessNumber(businessNumber)}
                        onChange={(e) => setBusinessNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                        placeholder={t('myStoreSetting.linkoPay.applyModal.placeholderBusinessNumber')} />
                    </BusinessNumberInputBox>
                    <ConfirmButton backgroundColor={theme.primary} onClick={onClickConfirmBusinessNumber}>
                      <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.subBackground}>
                        {t('myStoreSetting.linkoPay.applyModal.confirm')}
                      </Text>
                    </ConfirmButton>
                  </InlineControlRow>
                  :
                  <ReadonlyValueBox>
                    <ReadonlyValueText color={theme.text}>{businessNumber ? formatBusinessNumber(businessNumber) : '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
            {
              isEditable && businessStatusResult !== undefined &&
              <ErrorRow>
                <ErrorText
                  fontFamily='NotoSansKR-Medium'
                  color={businessStatusResult === 'active' ? theme.text : theme.pointRed}>
                  {
                    businessStatusResult === 'active' ?
                      t('myStoreSetting.linkoPay.applyModal.businessStatusActive')
                      :
                      (businessStatusResult === 'inactive' ?
                        t('myStoreSetting.linkoPay.applyModal.businessStatusInactive')
                        :
                        t('myStoreSetting.linkoPay.applyModal.errorBusinessNumber'))
                  }
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && businessStatusResult === undefined && isBusinessNumberInvalid &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.errorBusinessNumber')}
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && businessStatusResult === undefined && !isBusinessNumberInvalid && isSubmitAttempted && businessNumber &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.businessStatusVerifyRequired')}
                </ErrorText>
              </ErrorRow>
            }
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelBusinessLicense')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <FileBox
                    borderColor={(isRejected || (isSubmitAttempted && !businessLicenseFileName)) ? theme.pointRed : theme.line}
                    backgroundColor={theme.subBackground}
                    onClick={onClickFileBrowse}>
                    <FileIconImage color={theme.line} />
                    <FileNameText color={businessLicenseFileName ? theme.text : (isSubmitAttempted ? theme.pointRedAlpha20 : theme.line)}>
                      {businessLicenseFileName || t('myStoreSetting.linkoPay.applyModal.placeholderBusinessLicense')}
                    </FileNameText>
                    <FileBrowseText color={businessLicenseFileName ? theme.subText : (isSubmitAttempted ? theme.line : theme.primary)}>
                      {businessLicenseFileName ? t('myStoreSetting.linkoPay.applyModal.fileModify') : t('myStoreSetting.linkoPay.applyModal.fileBrowse')}
                    </FileBrowseText>
                    <HiddenFileInput
                      ref={BusinessLicenseInputRef}
                      type='file'
                      accept='.jpg,.jpeg,.png,.pdf,.heic,.hevc,image/jpeg,image/png,application/pdf'
                      onChange={onChangeBusinessLicense} />
                  </FileBox>
                  :
                  <ReadonlyFileRow
                    $clickable={!!businessLicenseUrl}
                    onClick={onClickReadonlyBusinessLicense}>
                    <FileIconImage color={theme.line} />
                    <ReadonlyValueText color={theme.text}>{businessLicenseFileName || '-'}</ReadonlyValueText>
                  </ReadonlyFileRow>
              }
            </FieldRow>
            {
              isRejected &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.rejectedBusinessLicense')}
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && !isRejected && isSubmitAttempted && !businessLicenseFileName &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.errorBusinessLicense')}
                </ErrorText>
              </ErrorRow>
            }
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelOwnerName')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InputBox
                    borderColor={isSubmitAttempted && !ownerName ? theme.pointRed : theme.line}
                    backgroundColor={theme.subBackground}>
                    <InputField
                      color={theme.text}
                      $autofillBackground={theme.subBackground}
                      $placeholderColor={isSubmitAttempted && !ownerName ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                      value={ownerName}
                      onChange={(e) => setOwnerName(e.target.value)}
                      placeholder={t('myStoreSetting.linkoPay.applyModal.placeholderOwnerName')} />
                  </InputBox>
                  :
                  <ReadonlyValueBox>
                    <ReadonlyValueText color={theme.text}>{ownerName || '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelBankAccount')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InlineControlRow>
                    <BankSelectBox
                      selectedBank={selectedBank}
                      setSelectedBank={onChangeSelectedBank} />
                    <SmallInputBox
                      borderColor={(isRejected || (isSubmitAttempted && !accountNumber)) ? theme.pointRed : theme.line}
                      backgroundColor={theme.subBackground}>
                      <InputField
                        color={theme.text}
                        $autofillBackground={theme.subBackground}
                        $placeholderColor={isSubmitAttempted && !accountNumber ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                        value={accountNumber}
                        onChange={onChangeAccountNumber}
                        placeholder={t('myStoreSetting.linkoPay.applyModal.placeholderAccount')} />
                    </SmallInputBox>
                    <ConfirmButton backgroundColor={theme.primary} onClick={onClickConfirmAccount}>
                      <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.subBackground}>
                        {t('myStoreSetting.linkoPay.applyModal.confirm')}
                      </Text>
                    </ConfirmButton>
                  </InlineControlRow>
                  :
                  <ReadonlyBankRow>
                    <ReadonlyBankLogo src={selectedBank?.logo || BankDefaultImage} alt={selectedBank?.name || ''} />
                    <AccountWrapper>
                      <ReadonlyValueText color={theme.text}>{accountNumber ? formatAccountNumber(selectedBank, accountNumber) : '-'}</ReadonlyValueText>
                    </AccountWrapper>
                    <ReadonlyValueText color={theme.text}>
                      {holderName || '-'}
                    </ReadonlyValueText>
                  </ReadonlyBankRow>
              }
            </FieldRow>
            {
              isEditable && holderName !== undefined &&
              <ErrorRow>
                <ErrorText
                  fontFamily='NotoSansKR-Medium'
                  color={holderName ? theme.text : theme.pointRed}>
                  {
                    holderName ?
                      `${t('myStoreSetting.linkoPay.applyModal.labelHolderName')}: ${holderName}`
                      :
                      t('myStoreSetting.linkoPay.applyModal.errorBankAccount')
                  }
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && isAccountNumberInvalid &&
              <ErrorRow>
                <ErrorText fontFamily='NotoSansKR-Medium' color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.invalidAccountNumber')}
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && holderName === undefined && isRejected &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.errorBankAccount')}
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && holderName === undefined && !isRejected && isSubmitAttempted && !selectedBank &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.bankRequired')}
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && holderName === undefined && !isRejected && isSubmitAttempted && selectedBank && !accountNumber &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.errorBankAccount')}
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && isAccountConfirmRequired &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.accountVerifyRequired')}
                </ErrorText>
              </ErrorRow>
            }
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelEmail')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InputBox
                    borderColor={isSubmitAttempted && !contactEmail ? theme.pointRed : theme.line}
                    backgroundColor={theme.subBackground}>
                    <InputField
                      color={theme.text}
                      $autofillBackground={theme.subBackground}
                      $placeholderColor={isSubmitAttempted && !contactEmail ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder={t('myStoreSetting.linkoPay.applyModal.placeholderEmail')} />
                  </InputBox>
                  :
                  <ReadonlyValueBox>
                    <ReadonlyValueText color={theme.text}>{contactEmail || '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelPhone')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InlineControlRow>
                    <CountryCodeSelectBox
                      selectedCountry={selectedCountry}
                      setSelectedCountry={setSelectedCountry} />
                    <SmallInputBox
                      borderColor={isSubmitAttempted && !contactPhone ? theme.pointRed : theme.line}
                      backgroundColor={theme.subBackground}>
                      <InputField
                        color={theme.text}
                        $autofillBackground={theme.subBackground}
                        $placeholderColor={isSubmitAttempted && !contactPhone ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                        type='tel'
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder={t('myStoreSetting.linkoPay.applyModal.placeholderPhone')} />
                    </SmallInputBox>
                    <ConfirmButton backgroundColor={theme.primary} onClick={onClickConfirmPhone}>
                      <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.subBackground}>
                        {t('myStoreSetting.linkoPay.applyModal.confirm')}
                      </Text>
                    </ConfirmButton>
                  </InlineControlRow>
                  :
                  <ReadonlyValueBox>
                    {
                      selectedCountry.flagUrl &&
                      <ReadonlyCountryFlag src={selectedCountry.flagUrl} alt='' />
                    }
                    <ReadonlyValueText color={theme.text}>{contactPhone || '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
            {
              isEditable && hasLookupResult &&
              <ErrorRow>
                <ErrorText
                  fontFamily='NotoSansKR-Medium'
                  color={lookupLinkAppId ? theme.text : theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.linkoAppPrefix')} {lookupLinkAppId ? lookupLinkAppId : t('myStoreSetting.linkoPay.applyModal.linkoAppNotJoined')}
                </ErrorText>
              </ErrorRow>
            }
            {
              isEditable && isPhoneConfirmRequired &&
              <ErrorRow>
                <ErrorText color={theme.pointRed}>
                  {t('myStoreSetting.linkoPay.applyModal.linkAppVerifyRequired')}
                </ErrorText>
              </ErrorRow>
            }
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                  {t('myStoreSetting.linkoPay.applyModal.labelSettlementCycle')}
                </Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <CycleButtonGroup>
                    <CycleButton
                      borderColor={selectedCycle === 5 ? theme.primary : theme.line}
                      backgroundColor={selectedCycle === 5 ? theme.primary : theme.subBackground}
                      onClick={() => setSelectedCycle(5)}>
                      <Text
                        fontFamily='NotoSansKR-Medium'
                        fontSize={14}
                        color={selectedCycle === 5 ? theme.subBackground : theme.text}>
                        {t('myStoreSetting.linkoPay.applyModal.cycleDay5')}
                      </Text>
                    </CycleButton>
                    <CycleButton
                      borderColor={selectedCycle === 7 ? theme.primary : theme.line}
                      backgroundColor={selectedCycle === 7 ? theme.primary : theme.subBackground}
                      onClick={() => setSelectedCycle(7)}>
                      <Text
                        fontFamily='NotoSansKR-Medium'
                        fontSize={14}
                        color={selectedCycle === 7 ? theme.subBackground : theme.text}>
                        {t('myStoreSetting.linkoPay.applyModal.cycleDay7')}
                      </Text>
                    </CycleButton>
                    <CycleButton
                      borderColor={selectedCycle === 14 ? theme.primary : theme.line}
                      backgroundColor={selectedCycle === 14 ? theme.primary : theme.subBackground}
                      onClick={() => setSelectedCycle(14)}>
                      <Text
                        fontFamily='NotoSansKR-Medium'
                        fontSize={14}
                        color={selectedCycle === 14 ? theme.subBackground : theme.text}>
                        {t('myStoreSetting.linkoPay.applyModal.cycleDay14')}
                      </Text>
                    </CycleButton>
                  </CycleButtonGroup>
                  :
                  <ReadonlyValueBox>
                    <ReadonlyValueText color={theme.text}>{t('myStoreSetting.linkoPay.paymentDate')}{selectedCycle}{t('myStoreSetting.linkoPay.days')}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
          </FieldBlock>

          {
            showTerms &&
            <SectionHeaderRow>
              <SectionDot backgroundColor={theme.primary} />
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                {t('myStoreSetting.linkoPay.applyModal.termsTitle')}
              </Text>
            </SectionHeaderRow>
          }

          {
            showTerms &&
            <TermsGroup borderColor={theme.line} backgroundColor={theme.subBackground}>
              <TermsRow>
                <Checkbox
                  borderColor={isAgreedAll ? theme.primary : theme.line}
                  backgroundColor={isAgreedAll ? theme.primary : theme.subBackground}
                  onClick={onClickAgreeAll}>
                  {
                    isAgreedAll &&
                    <CheckIcon color={theme.subBackground} />
                  }
                </Checkbox>
                <Text
                  fontFamily='NotoSansKR-Medium'
                  fontSize={14}
                  color={theme.text}
                  style={{ cursor: 'pointer' }}
                  onClick={onClickAgreeAll}>
                  {t('myStoreSetting.linkoPay.applyModal.termsAgreeAll')}
                </Text>
              </TermsRow>
              <TermsDivider backgroundColor={theme.line} />
              {
                termsItems.map(item => (
                  <React.Fragment key={'TermsRow-' + item.key}>
                    <TermsRow>
                      <Checkbox
                        borderColor={item.checked ? theme.primary : theme.line}
                        backgroundColor={item.checked ? theme.primary : theme.subBackground}
                        onClick={() => item.setChecked(!item.checked)}>
                        {
                          item.checked &&
                          <CheckIcon color={theme.subBackground} />
                        }
                      </Checkbox>
                      <TermsLabelRow>
                        <Text
                          fontFamily='NotoSansKR-Medium'
                          fontSize={14}
                          color={theme.text}
                          style={{ cursor: 'pointer' }}
                          onClick={() => item.setChecked(!item.checked)}>
                          {t('myStoreSetting.linkoPay.applyModal.' + item.labelKey)}
                        </Text>
                        <Text
                          fontFamily='NotoSansKR-Regular'
                          fontSize={14}
                          color={theme.gray}
                          style={{ cursor: 'pointer' }}
                          onClick={() => onToggleExpandedTerm(item.key)}>
                          {
                            expandedTerms[item.key] ?
                              t('myStoreSetting.linkoPay.applyModal.termsClose')
                              :
                              t('myStoreSetting.linkoPay.applyModal.termsView')
                          }
                        </Text>
                      </TermsLabelRow>
                    </TermsRow>
                    <TermsContentRow $open={!!expandedTerms[item.key]}>
                      <TermsContentInner>
                        <CheckboxSpacer />
                        <Text
                          fontFamily='NotoSansKR-Light'
                          fontSize={14}
                          color={theme.text}
                          style={{ flex: '1 0 0', minWidth: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 'normal' }}>
                          {t('myStoreSetting.linkoPay.applyModal.' + item.contentKey)}
                        </Text>
                      </TermsContentInner>
                    </TermsContentRow>
                  </React.Fragment>
                ))
              }
            </TermsGroup>
          }
          {
            showTerms && isSubmitAttempted && !isAgreedAll &&
            <ErrorRow>
              <ErrorText color={theme.pointRed}>
                {t('myStoreSetting.linkoPay.applyModal.termsRequired')}
              </ErrorText>
            </ErrorRow>
          }
        </ScrollableContent>

        <ButtonRow>
          <CloseButton backgroundColor={theme.buttonFill} onClick={onClickExit}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('myStoreSetting.linkoPay.applyModal.close')}
            </Text>
          </CloseButton>
          <SubmitButton backgroundColor={theme.primary} onClick={onClickSubmitButton}>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.subBackground}>
              {(isApply || editingOverride) ? t('myStoreSetting.linkoPay.applyModal.submit') : t('myStoreSetting.linkoPay.applyModal.submitResubmit')}
            </Text>
          </SubmitButton>
        </ButtonRow>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 504px;
  max-height: 90vh;
  padding: 20px 0;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;

  flex-shrink: 0;
`;

const StatusPillRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 10px;

  width: 100%;
  padding: 10px 20px 5px 20px;
  box-sizing: border-box;
`;

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  flex: 1 1 auto;
  min-height: 0;

  width: 100%;
  padding: 0 20px 40px 20px;
  box-sizing: border-box;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.scrollBarColor};

    border-radius: 50px;
  }

  &::-webkit-scrollbar-track {
    margin: 4px 0;

    background-color: transparent;

    border-radius: 300px;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: transparent;
  }
`;

const StatusPill = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;

  flex: 1 0 0;
  min-width: 0;

  padding: 5px 20px;
  box-sizing: border-box;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};
`;

const StatusPillInactive = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;

  flex: 1 0 0;
  min-width: 0;

  padding: 5px 20px;
  box-sizing: border-box;

  border-radius: 5px;
`;

const StatusDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 1000px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const SectionHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;

  padding-top: 10px;
`;

const SectionDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 1px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 5px;

  width: 100%;

  padding-bottom: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  width: 100%;
  height: 35px;
`;

const ErrorRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;
`;

const ErrorText = styled.p`
  font-family: ${props => props.fontFamily || 'NotoSansKR-Regular'};
  font-size: 14px;

  margin: 0 0 0 0;

  color: ${props => props.color};
`;

const FieldLabelBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  flex: 1 0 0;
  min-width: 0;
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 291px;
  height: 100%;
  padding: 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const BusinessNumberInputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 235px;
  height: 100%;
  padding: 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const SmallInputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 160px;
  height: 100%;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const InputField = styled.input`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  outline: none;
  border: 0px;

  width: 100%;
  height: 100%;

  color: ${props => props.color};
  background-color: transparent;

  &::placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
    -webkit-text-fill-color: ${props => props.$placeholderColor};
  }
  &::-webkit-input-placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
    -webkit-text-fill-color: ${props => props.$placeholderColor};
  }
  &::-moz-placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
  }
  &:-ms-input-placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px ${props => props.$autofillBackground} inset !important;
    -webkit-text-fill-color: ${props => props.color} !important;
    caret-color: ${props => props.color};
    transition: background-color 99999s ease-out;
  }
`;

const FileBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 291px;
  height: 100%;
  padding: 7px 10px 7px 5px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileIconImage = styled(UploadDocument)`
  width: 22px;
  height: 20px;

  flex-shrink: 0;

  path {
    stroke: ${props => props.color};
  }
`;

const FileNameText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  flex: 1 0 0;
  min-width: 0;

  color: ${props => props.color};

  margin: 0 0 0 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileBrowseText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const InlineControlRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  height: 100%;

  flex-shrink: 0;
`;

const ConfirmButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 51px;
  height: 32px;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const CycleButtonGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  width: 291px;
  height: 100%;

  flex-shrink: 0;
`;

const CycleButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;
  height: 100%;

  padding: 10px 0;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const TermsGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 100%;
  padding: 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const TermsRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
  height: 21px;
`;

const TermsLabelRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;
`;

const TermsContentRow = styled.div`
  display: grid;
  grid-template-rows: ${props => props.$open ? '1fr' : '0fr'};

  width: 100%;

  margin-top: ${props => props.$open ? '0' : '-10px'};
  opacity: ${props => props.$open ? 1 : 0};
  transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              margin-top 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.25s ease;
`;

const TermsContentInner = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 10px;

  min-height: 0;
  overflow: hidden;
`;

const CheckboxSpacer = styled.div`
  width: 21px;
  height: 21px;

  flex-shrink: 0;
`;

const TermsDivider = styled.div`
  width: 100%;
  height: 1px;

  border-radius: 300px;
  background-color: ${props => props.backgroundColor};
`;

const Checkbox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 21px;
  height: 21px;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const CheckIcon = styled(CheckSvg)`
  width: 12px;
  height: auto;

  color: ${props => props.color};
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: stretch;
  align-items: center;

  gap: 10px;

  width: 100%;
  padding: 10px 20px 0 20px;
  box-sizing: border-box;

  flex-shrink: 0;
`;

const CloseButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 90px;
  height: 40px;
  padding: 5px 16px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SubmitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 40px;
  padding: 5px 16px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const ReadonlyValueBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 291px;
  height: 100%;

  flex-shrink: 0;
`;

const ReadonlyValueText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const ReadonlyFileRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 291px;
  height: 100%;

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    }
  }
`;

const ReadonlyBankRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 291px;
  height: 100%;

  flex-shrink: 0;

  & p {
    min-width: 0;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ReadonlyBankLogo = styled.img`
  width: 32px;
  height: 23px;

  object-fit: contain;
  border-radius: 3px;

  flex-shrink: 0;
`;

const AccountWrapper = styled.div`
  width: 160px;

  flex-shrink: 0;

  overflow: hidden;
`;

const ReadonlyCountryFlag = styled.img`
  width: 32px;
  height: 22px;

  object-fit: cover;
  border-radius: 5px;

  flex-shrink: 0;
`;
